import { useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

export type UrlFilterValue = string | number | boolean | null | Record<string, string | number | boolean | null>;
export type UrlFilters = Record<string, UrlFilterValue>;
interface UrlFilterField {
  fieldName: string;
  multiselect: boolean;
}

export function parseUrlFilters(raw: string | null | undefined, legacy = false): UrlFilters {
  if (!raw) return {};
  const value: unknown = JSON.parse(legacy ? decodeURIComponent(raw) : raw);
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SyntaxError('URL filters must be a JSON object.');
  }
  for (const entry of Object.values(value)) {
    if (Array.isArray(entry) || (entry && typeof entry === 'object' &&
      Object.values(entry).some(item => item !== null && typeof item === 'object'))) {
      throw new SyntaxError('URL filter values must be scalars or multiselect objects.');
    }
  }
  return value as UrlFilters;
}

export function useListUrlOptions(filterFields: UrlFilterField[] = []) {
  const location = useLocation();
  const { filter } = useParams();
  const fieldsKey = JSON.stringify(filterFields);
  const result = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search);
      const filters = { ...parseUrlFilters(filter, true), ...parseUrlFilters(params.get('filters')) };
      const fields: UrlFilterField[] = JSON.parse(fieldsKey);
      fields.forEach(({ fieldName, multiselect }) => {
        if (!Object.prototype.hasOwnProperty.call(filters, fieldName)) return;
        const value = filters[fieldName];
        if (multiselect ? !value || typeof value !== 'object' : value !== null && typeof value === 'object') {
          throw new SyntaxError(multiselect
            ? `The ${fieldName} multiselect filter must be an object keyed by option IDs.`
            : `The ${fieldName} filter must be a scalar value.`);
        }
      });
      return { filters, error: null };
    } catch (error) {
      if (!(error instanceof SyntaxError) && !(error instanceof URIError)) throw error;
      return { filters: {}, error: `Invalid URL filters: ${error.message}` };
    }
  }, [location.search, filter, fieldsKey]);
  useEffect(() => {
    if (result.error) toast.error(result.error, { id: 'invalid-url-filters' });
  }, [result.error]);
  return {
    filters: result.filters,
    search: new URLSearchParams(location.search).get('search') ?? '',
    urlKey: `${location.key}:${location.pathname}${location.search}`,
  };
}
